import React, { useEffect, useState, useMemo } from "react";

// Prop types remain the same, but sizePx can be a number for better validation.
interface customQueryProps {
    queryType: "max-width" | "min-width" | "max-height" | "min-height";
    sizePx: number; // Changed to number
    initialState?: boolean;
}

interface defaultQueryProps {
    queryType: "mobile" | "medium-screen" | "desktop";
    initialState?: boolean;
}

type Props = customQueryProps | defaultQueryProps;

const useMediaQuery = (props: Props) => {
    // 1. Memoize the query string for performance and type safety.
    const query = useMemo(() => {
        const { queryType } = props;
        switch (queryType) {
            case "max-width":
            case "min-width":
            case "max-height":
            case "min-height":
                // Safely access sizePx only for custom query types
                return `(${queryType}: ${props.sizePx}px)`;
            case "mobile":
                return `(max-width: 576px)`;
            case "medium-screen":
                return `(max-width: 991px)`;
            case "desktop":
                return `(min-width: 992px)`;
            default:
                return "";
        }
    }, [props]); // Recalculate only when props change.

    // Keep SSR and first client render aligned — read viewport only after mount.
    const [matches, setMatches] = useState(props.initialState ?? false);

    useEffect(() => {
        if (typeof window === "undefined" || !query) return;

        const mediaQueryList = window.matchMedia(query);

        const listener = (event: MediaQueryListEvent) => {
            setMatches(event.matches);
        };

        setMatches(mediaQueryList.matches);
        mediaQueryList.addEventListener("change", listener);

        return () => {
            mediaQueryList.removeEventListener("change", listener);
        };
    }, [query]);

    return matches;
};

export default useMediaQuery;
